import type { Section } from "./types";

export const FORM_SLUG = "upside-mentor-application";

export const SECTIONS: Section[] = [
  {
    id: "about-you",
    title: "Tell us about you",
    description: "A few details so we know who you are and how to get in touch.",
    fields: [
      { type: "text", id: "mentor_first_name", label: "First name", required: true },
      { type: "text", id: "mentor_last_name", label: "Last name", required: true },
      {
        type: "text",
        id: "mentor_preferred_name",
        label: "Preferred name",
        placeholder: "What you like to be called",
        required: true,
      },
      { type: "date", id: "mentor_date_of_birth", label: "Date of birth", required: true },
      { type: "email", id: "mentor_email", label: "Email", required: true },
      { type: "phone", id: "mentor_phone", label: "Phone number", required: true },
      { type: "address", id: "mentor_address", label: "Address", required: true },
    ],
  },
  {
    id: "why-mentor",
    title: "Why do you want to be a mentor?",
    description: "Tick anything that resonates — you can choose more than one.",
    fields: [
      {
        type: "multi-select",
        id: "mentor_reasons",
        label: "Why do you want to be a mentor?",
        required: true,
        options: [
          "Make a difference",
          "Enjoy working with young people",
          "Give back",
          "Had a mentor",
          "Other",
        ],
      },
      {
        type: "textarea",
        id: "mentor_reasons_detail",
        label: "Tell us more",
        placeholder: "Anything you'd like to add about why you want to mentor",
        required: true,
        maxLength: 1500,
      },
    ],
  },
  {
    id: "interests",
    title: "Tell us about your interests",
    description: "We want to learn cool things about you to help us match you with the right young person!",
    fields: [
      {
        type: "multi-select",
        id: "mentor_interests",
        label: "What are you into? (tick all that apply)",
        required: true,
        options: [
          "Sport / Fitness",
          "Gaming",
          "Music",
          "Art / Creativity",
          "Reading",
          "Outdoors / Nature",
          "Movies / TV",
          "Cooking / Food",
          "Tech",
          "Animals",
          "Happy to try anything",
        ],
      },
      {
        type: "textarea",
        id: "mentor_spare_time",
        label: "What do you enjoy doing in your spare time?",
        required: true,
        maxLength: 1500,
      },
    ],
  },
  {
    id: "describe-you",
    title: "Which words would you use to describe who you are?",
    description: "Pick any that feel right — you can choose as many as you like in each group.",
    fields: [
      {
        type: "multi-select",
        id: "mentor_personality",
        label: "Personality",
        required: true,
        options: [
          "Introvert",
          "Quiet",
          "Outgoing",
          "Confident",
          "Friendly",
          "Warm",
          "Thoughtful",
          "Reflective",
          "Energetic",
          "Calm",
          "Independent",
          "Sensitive",
        ],
      },
      {
        type: "multi-select",
        id: "mentor_social_style",
        label: "What would people say about you",
        required: true,
        options: [
          "Talkative",
          "Good communicator",
          "Warms up in conversation",
          "Finds it hard to express feelings",
          "Builds trust slowly",
          "Loyal",
          "Enjoys being around others",
          "Prefers one-on-one interactions",
        ],
      },
      {
        type: "multi-select",
        id: "mentor_strengths",
        label: "Strengths and positive traits",
        required: true,
        options: [
          "Kind",
          "Caring",
          "Respectful",
          "Creative",
          "Curious",
          "Resilient",
          "Determined",
          "Motivated",
          "Responsible",
          "Leader",
          "Helpful",
          "Empathetic",
        ],
      },
      {
        type: "textarea",
        id: "mentor_about_more",
        label: "Tell us more",
        placeholder: "Anything else that would help us get to know you",
        required: true,
        maxLength: 1500,
      },
    ],
  },
  {
    id: "conversations",
    title: "Conversations you could help with",
    description:
      "We want to empower belief in our young people. Which conversations do you feel you could help a young person with if they brought it up?",
    fields: [
      {
        type: "multi-select",
        id: "mentor_conversation_topics",
        label: "Tick anything you feel you could support",
        required: true,
        options: [
          "Goals",
          "Dreams",
          "Confidence",
          "Friendships",
          "School motivation",
          "Stress / anxiety",
          "How to get support",
          "Better self talk",
          "Just being there",
        ],
      },
      {
        type: "textarea",
        id: "mentor_youth_experience",
        label: "Experience with young people",
        placeholder: "Tell us about any experience you have working or spending time with young people",
        required: true,
        maxLength: 1500,
      },
    ],
  },
  {
    id: "matching",
    title: "Mentor match preferences",
    description:
      "We want to match you well with a young person. Do you think you could work well with a young person that is:",
    fields: [
      {
        type: "multi-select",
        id: "mentor_match_preferences",
        label: "Tick all that apply",
        required: true,
        options: [
          "Quiet / shy",
          "Active / energetic",
          "Creative",
          "Needs encouragement",
          "Low confidence",
          "Talkative",
          "Can work with most young people",
        ],
      },
    ],
  },
  {
    id: "commitment",
    title: "Commitment & technology",
    description: "A couple of quick yes/no questions about the practical side.",
    fields: [
      {
        type: "radio",
        id: "mentor_commitment",
        label:
          "The programme runs for ten weeks, at a consistent day and time during the school day. We'll work with you to find a regular time that aligns as best as possible with both your availability and the young person's school schedule.",
        helper: "Consistency matters — it's how trust builds with the young person.",
        required: true,
        options: ["Yes", "No"],
      },
      {
        type: "radio",
        id: "mentor_tech_access",
        label:
          "As this is an online programme, will you have access to a reliable device with video and audio capability and reliable internet?",
        required: true,
        options: ["Yes", "No"],
      },
    ],
  },
  {
    id: "final-reflection",
    title: "Final reflection",
    description: "One last question to wrap up.",
    fields: [
      {
        type: "textarea",
        id: "mentor_success_definition",
        label: "What would success look like for you as a mentor?",
        required: true,
        minLength: 10,
        maxLength: 2000,
      },
      {
        type: "consent",
        id: "mentor_consent",
        label:
          'I confirm the information I\'ve provided is accurate and I consent to Upside processing this application and contacting me about it. See our <a href="https://upside.org.nz/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.',
        required: true,
      },
    ],
  },
];
